import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Skeleton,
  Paper,
  Autocomplete,
  TextField,
  Divider,
  alpha,
  useTheme,
  Stack,
  Chip,
} from '@mui/material';
import { Edit, Check, Close, Public, CalendarMonth, AttachMoney, Schedule } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { schoolService, SchoolSettings as SchoolSettingsType } from '../../services/schoolService';
import { useAuth } from '../../context/AuthContext';

interface TimezoneOption {
  value: string;
  label: string;
}

interface DateFormatOption {
  value: string;
  label: string;
  example: string;
}

interface CurrencyOption {
  value: string;
  label: string;
  symbol: string;
}

interface MonthOption {
  value: number;
  label: string;
}

const dateFormatOptions: DateFormatOption[] = [
  { value: 'dd/MM/yyyy', label: 'DD/MM/YYYY', example: '31/12/2024' },
  { value: 'MM/dd/yyyy', label: 'MM/DD/YYYY', example: '12/31/2024' },
  { value: 'yyyy-MM-dd', label: 'YYYY-MM-DD', example: '2024-12-31' },
  { value: 'dd MMM yyyy', label: 'DD MMM YYYY', example: '31 Dec 2024' },
];

const currencyOptions: CurrencyOption[] = [
  { value: 'PKR', label: 'Pakistani Rupee', symbol: 'Rs.' },
  { value: 'INR', label: 'Indian Rupee', symbol: '₹' },
  { value: 'AED', label: 'UAE Dirham', symbol: 'د.إ' },
  { value: 'SAR', label: 'Saudi Riyal', symbol: '﷼' },
  { value: 'USD', label: 'US Dollar', symbol: '$' },
  { value: 'GBP', label: 'British Pound', symbol: '£' },
  { value: 'EUR', label: 'Euro', symbol: '€' },
];

const monthOptions: MonthOption[] = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const SchoolSettings: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { updateUser, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState<SchoolSettingsType>({
    timezone: 'Asia/Karachi',
    dateFormat: 'dd/MM/yyyy',
    currency: 'PKR',
    academicYearStart: 4,
    feeBillingDay: 1,
  });
  const [editSettings, setEditSettings] = useState<SchoolSettingsType>(settings);
  const [timezoneOptions, setTimezoneOptions] = useState<TimezoneOption[]>([]);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const data = await schoolService.getSchoolSettings();
      if (data) {
        setSettings(data.settings);
        setEditSettings(data.settings);
        // Convert supported timezones to options
        const tzOptions = Object.entries(data.supportedTimezones || {}).map(([value, label]) => ({
          value,
          label: label as string,
        }));
        setTimezoneOptions(tzOptions);
      }
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      enqueueSnackbar('Failed to load settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleEdit = () => {
    setEditSettings({ ...settings });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditSettings({ ...settings });
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await schoolService.updateSchoolSettings(editSettings);
      setSettings(editSettings);
      setIsEditing(false);

      // Update the user context to reflect new timezone
      if (user && user.schoolId) {
        const updatedUser = {
          ...user,
          schoolId: {
            ...user.schoolId,
            settings: editSettings,
          },
        };
        updateUser(updatedUser);
      }

      enqueueSnackbar('Settings updated successfully', { variant: 'success' });
    } catch (error: any) {
      console.error('Error saving settings:', error);
      enqueueSnackbar(error.message || 'Failed to save settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getTimezoneLabel = (value: string) => {
    return timezoneOptions.find((tz) => tz.value === value)?.label || value;
  };

  const getDateFormatLabel = (value: string) => {
    const option = dateFormatOptions.find((df) => df.value === value);
    return option ? `${option.label} (${option.example})` : value;
  };

  const getCurrencyLabel = (value: string) => {
    const option = currencyOptions.find((c) => c.value === value);
    return option ? `${option.symbol} ${option.label} (${option.value})` : value;
  };

  const getMonthLabel = (value: number) => {
    return monthOptions.find((m) => m.value === value)?.label || '';
  };

  // Day-of-month picker (1-28) — capped at 28 so every month has that day.
  const dayOptions = Array.from({ length: 28 }, (_, i) => {
    const n = i + 1;
    const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th'
      : n % 10 === 1 ? 'st'
      : n % 10 === 2 ? 'nd'
      : n % 10 === 3 ? 'rd'
      : 'th';
    return { value: n, label: `${n}${suffix} of the month` };
  });
  const getDayLabel = (value?: number) =>
    dayOptions.find((d) => d.value === value)?.label || '1st of the month';

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const SettingRow = ({
    icon,
    label,
    value,
    editComponent,
    hint,
  }: {
    icon: React.ReactNode;
    label: string;
    value: string;
    editComponent: React.ReactNode;
    hint?: string;
  }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        py: 2.5,
        px: 3,
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        },
        transition: 'background-color 0.2s',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
          mr: 2,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 0.5, fontWeight: 500, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 0.5 }}
        >
          {label}
        </Typography>
        {isEditing ? (
          editComponent
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {value}
          </Typography>
        )}
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, maxWidth: 640, lineHeight: 1.45 }}>
            {hint}
          </Typography>
        )}
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
        {!isEditing ? (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={handleEdit}
            sx={{ borderRadius: 2 }}
          >
            Edit Settings
          </Button>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<Close />}
              onClick={handleCancel}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<Check />}
              onClick={handleSave}
              disabled={saving}
              sx={{ borderRadius: 2 }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </Box>

      <Paper 
        elevation={0} 
        sx={{ 
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <SettingRow
          icon={<Public fontSize="small" />}
          label="Timezone"
          value={getTimezoneLabel(settings.timezone)}
          editComponent={
            <Autocomplete
              size="small"
              options={timezoneOptions}
              value={timezoneOptions.find((tz) => tz.value === editSettings.timezone) || undefined}
              onChange={(_, newValue) => {
                if (newValue) {
                  setEditSettings({ ...editSettings, timezone: newValue.value });
                }
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select timezone"
                  sx={{ maxWidth: 400 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.value}
                    </Typography>
                  </Box>
                </Box>
              )}
              disableClearable
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<CalendarMonth fontSize="small" />}
          label="Date Format"
          value={getDateFormatLabel(settings.dateFormat)}
          editComponent={
            <Autocomplete
              size="small"
              options={dateFormatOptions}
              value={dateFormatOptions.find((df) => df.value === editSettings.dateFormat) || undefined}
              onChange={(_, newValue) => {
                if (newValue) {
                  setEditSettings({ ...editSettings, dateFormat: newValue.value });
                }
              }}
              getOptionLabel={(option) => `${option.label} (${option.example})`}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select date format"
                  sx={{ maxWidth: 400 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2">{option.label}</Typography>
                    <Chip label={option.example} size="small" variant="outlined" />
                  </Box>
                </Box>
              )}
              disableClearable
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<AttachMoney fontSize="small" />}
          label="Currency"
          value={getCurrencyLabel(settings.currency)}
          editComponent={
            <Autocomplete
              size="small"
              options={currencyOptions}
              value={currencyOptions.find((c) => c.value === editSettings.currency) || undefined}
              onChange={(_, newValue) => {
                if (newValue) {
                  setEditSettings({ ...editSettings, currency: newValue.value });
                }
              }}
              getOptionLabel={(option) => `${option.label} (${option.value})`}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select currency"
                  sx={{ maxWidth: 400 }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                      }}
                    >
                      {option.symbol}
                    </Box>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.value}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              disableClearable
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<Schedule fontSize="small" />}
          label="Academic Year Starts"
          value={getMonthLabel(settings.academicYearStart)}
          editComponent={
            <Autocomplete
              size="small"
              options={monthOptions}
              value={monthOptions.find((m) => m.value === editSettings.academicYearStart) || undefined}
              onChange={(_, newValue) => {
                if (newValue) {
                  setEditSettings({ ...editSettings, academicYearStart: newValue.value });
                }
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select month"
                  sx={{ maxWidth: 400 }}
                />
              )}
              disableClearable
            />
          }
        />

        <Divider />

        <SettingRow
          icon={<CalendarMonth fontSize="small" />}
          label="Fee Billing Day"
          value={getDayLabel(settings.feeBillingDay)}
          hint="The day of the month every fee installment lands on. Monthly bills always fall on this day; quarterly / termly bills land on this day of each period's first month. Capped at the 28th so every calendar month is safe."
          editComponent={
            <Autocomplete
              size="small"
              options={dayOptions}
              value={dayOptions.find((d) => d.value === (editSettings.feeBillingDay ?? 1)) || undefined}
              onChange={(_, newValue) => {
                if (newValue) {
                  setEditSettings({ ...editSettings, feeBillingDay: newValue.value });
                }
              }}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select day"
                  sx={{ maxWidth: 400 }}
                />
              )}
              disableClearable
            />
          }
        />
      </Paper>
    </Box>
  );
};

export default SchoolSettings;
